/**
 * The official Mega Coffee wordmark.
 *
 * Two files, from the same kit the site and the POS use
 * (`MegaCoffee/.../public/brand`). The lockup is ink + a yellow cup, so the
 * cup vanishes on `--color-brand` and the all-ink mono is the one that kit
 * names for yellow. Paper keeps the colour mark.
 */
import wordmark from '@/assets/mega-logo.png'
import wordmarkMono from '@/assets/mega-logo-mono.png'
import { inShell, requestExit } from '@/shell/native'

/** Taps that open the exit pad, and the gap that gives up on counting. */
const TAPS = 10
const GAP_MS = 1500

/*
 * The count lives out here rather than in a ref, so it survives the mark being
 * unmounted. The welcome screen's mark is inside the panel that starts an
 * order, so tapping it changes screens — and a counter that reset on every
 * remount would mean the gesture only ever worked on screens where nothing
 * happens, which is not a rule anybody could be taught over a phone.
 */
let taps = 0
let last = 0

export function Wordmark({
  className = '',
  on = 'paper'
}: {
  className?: string
  /** The ground the mark sits on. Yellow takes the espresso-ink mono. */
  on?: 'paper' | 'brand'
}) {
  /*
   * Ten taps on the mark asks the shell for the exit keypad.
   *
   * **It is a doorbell and not a key.** Nothing here decides anything: the pad
   * is a window the shell serves itself, the PIN is compared in Rust, and the
   * backoff after three wrong attempts is the same one Alt+F4 meets. So a
   * customer who finds this gets the keypad, which is exactly what a customer
   * who finds a keyboard already gets — and on a locked machine with no
   * keyboard, which is most of the fleet, it is the only thing an attendant
   * has.
   *
   * The handler neither stops the event nor prevents its default, because on
   * the welcome screen the mark sits inside the panel that starts an order and
   * the tap belongs to that panel. Counting is all this does.
   */
  const tap = () => {
    if (!inShell()) return
    const now = Date.now()
    taps = now - last > GAP_MS ? 1 : taps + 1
    last = now
    if (taps < TAPS) return
    taps = 0
    // Refused on an unlocked window and on one with no PIN — the shell decides,
    // and says so in its own log. There is nothing for the page to report.
    void requestExit().catch(() => {})
  }

  return (
    <img
      src={on === 'brand' ? wordmarkMono : wordmark}
      alt="Mega Coffee"
      onClick={tap}
      /*
       * Width, not height. The official lockup is ~4:1 and the screens it
       * sits on differ by an order of magnitude in how much room they mean
       * to give it; a height that reads on the attract screen is a stamp
       * on the menu bar.
       */
      className={`h-auto object-contain ${className}`}
      width={380}
      height={97}
    />
  )
}
